// A small solver for "is this deal winnable?" (the not-annoying deal, owner 2026-10-17: after two deals in a
// row that the player did not win, the next one is one that can be won). A depth-first search over the same
// pure moves the game uses (reducer.ts), with a set of positions already seen and a node budget: `won` means a
// winning line was found (the deal is winnable), `stuck` that every line was tried (it is not), `unknown`
// that the budget ran out (treated as "not proven"). Good moves first: to a foundation, then moves that turn
// a face-down card or empty a column, then the other column moves, the waste, and the stock last.
// Framework-free (it runs in a Web Worker, engine/solver.worker.ts) and deterministic.
import type { GameId, GameState, PileRef } from './types';
import {
  canLift,
  clickStock,
  createGameState,
  deal,
  hasFaceDownCard,
  legalTargets,
  moveRun,
  rulesOf,
  sendToFoundation,
} from './reducer';
import { card } from './rules/base';

export type SolveResult = 'won' | 'stuck' | 'unknown';

/** The outcome and how far the search got (the best position's score: an unproven deal's ease). */
export interface Solved {
  result: SolveResult;
  best: number;
}

/** A position as a string: the columns (face-down cards marked), the foundations' sizes, the stock and waste. */
function keyOf(s: GameState): string {
  const cols = s.piles.tableau.map((col) => col.map((k) => (s.faceUp[k] ? k : `~${k}`)).join(',')).join('|');
  const found = s.piles.foundations.map((f) => f.length).join(',');
  return `${cols}#${found}#${s.piles.stock.join(',')}#${s.piles.waste.join(',')}`;
}

const T = (index: number): PileRef => ({ kind: 'tableau', index });

/** The moves from `s`, best first, as the positions they lead to. */
function successors(s: GameState): GameState[] {
  const found: GameState[] = [];
  const reveal: GameState[] = [];
  const other: GameState[] = [];
  const fromWaste: GameState[] = [];
  const base = { ...s, history: [] }; // no undo history in the search (it would be copied on every move)
  const rules = rulesOf(base);
  const cols = base.piles.tableau;
  // a column's top card or the waste's to a foundation
  cols.forEach((col, c) => {
    if (!col.length) return;
    const n = sendToFoundation(base, { pile: T(c), index: col.length - 1 });
    if (n) found.push(n);
  });
  const waste = base.piles.waste;
  if (waste.length) {
    const n = sendToFoundation(base, { pile: { kind: 'waste' }, index: waste.length - 1 });
    if (n) found.push(n);
  }
  // runs between columns
  cols.forEach((col, c) => {
    for (let i = 0; i < col.length; i++) {
      if (!base.faceUp[col[i]!] || !canLift(base, T(c), i)) continue;
      for (const to of legalTargets(base, { pile: T(c), index: i })) {
        if (to.kind !== 'tableau') continue;
        const target = cols[to.index]!;
        // a whole column onto an empty one changes nothing
        if (i === 0 && target.length === 0) continue;
        const n = moveRun(base, { pile: T(c), index: i }, to);
        if (!n) continue;
        const under = i > 0 ? col[i - 1]! : undefined;
        if (i === 0 || (under !== undefined && !base.faceUp[under])) reveal.push(n);
        // a run that already lies on the card it builds on only moves if that frees the card for a
        // foundation (else it just shuffles between equal places: the search space blows up on it)
        else if (under !== undefined && rules.buildsOn(card(base, col[i]!), card(base, under))) {
          if (sendToFoundation(n, { pile: T(c), index: i - 1 })) other.push(n);
        } else other.push(n);
      }
    }
  });
  if (waste.length) {
    for (const to of legalTargets(base, { pile: { kind: 'waste' }, index: waste.length - 1 })) {
      if (to.kind !== 'tableau') continue;
      const n = moveRun(base, { pile: { kind: 'waste' }, index: waste.length - 1 }, to);
      if (n) fromWaste.push(n);
    }
  }
  const out = [...found, ...reveal, ...fromWaste, ...other];
  const stock = clickStock(base);
  if (stock) out.push(stock);
  return out;
}

/** Klondike / Double Klondike: no face-down card and nothing left in the stock and waste always plays out. */
function certainWin(s: GameState): boolean {
  return (
    (s.game.startsWith('klondike') || s.game.startsWith('double-klondike')) &&
    !s.piles.stock.length &&
    !s.piles.waste.length &&
    !hasFaceDownCard(s)
  );
}

/** How promising a position looks: cards home, cards turned, ordered runs (same suit counts double), empty
 *  columns. The search always goes on from the best position it has (best-first). */
function score(s: GameState): number {
  const rules = rulesOf(s);
  let v = 0;
  for (const f of s.piles.foundations) v += f.length * 12;
  for (const col of s.piles.tableau) {
    if (!col.length) {
      v += 4;
      continue;
    }
    for (let i = 0; i < col.length; i++) {
      const k = col[i]!;
      if (!s.faceUp[k]) {
        v -= 6;
        continue;
      }
      const below = i > 0 ? col[i - 1]! : undefined;
      if (below !== undefined && s.faceUp[below]) {
        const a = card(s, k);
        const b = card(s, below);
        if (rules.buildsOn(a, b)) v += a.suit === b.suit ? 3 : 1;
      }
    }
  }
  return v - s.piles.stock.length * 0.5;
}

/** A binary max-heap of positions by score. */
class Heap {
  private items: { s: GameState; v: number }[] = [];
  get size() {
    return this.items.length;
  }
  push(s: GameState, v: number) {
    const a = this.items;
    a.push({ s, v });
    let i = a.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (a[p]!.v >= a[i]!.v) break;
      [a[p], a[i]] = [a[i]!, a[p]!];
      i = p;
    }
  }
  pop(): GameState | undefined {
    const a = this.items;
    if (!a.length) return undefined;
    const top = a[0]!.s;
    const last = a.pop()!;
    if (a.length) {
      a[0] = last;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        const r = l + 1;
        let m = i;
        if (l < a.length && a[l]!.v > a[m]!.v) m = l;
        if (r < a.length && a[r]!.v > a[m]!.v) m = r;
        if (m === i) break;
        [a[m], a[i]] = [a[i]!, a[m]!];
        i = m;
      }
    }
    return top;
  }
}

/** Searches for a win from `start` within `maxNodes` positions (best-first). */
export function solve(start: GameState, maxNodes = 60_000): Solved {
  const seen = new Set<string>([keyOf(start)]);
  const open = new Heap();
  let best = score(start);
  open.push(start, best);
  let nodes = 0;
  while (open.size) {
    const s = open.pop()!;
    if (++nodes > maxNodes) return { result: 'unknown', best };
    for (const next of successors(s)) {
      if (next.won || certainWin(next)) return { result: 'won', best: Infinity };
      const key = keyOf(next);
      if (seen.has(key)) continue;
      seen.add(key);
      const v = score(next);
      if (v > best) best = v;
      open.push(next, v);
    }
  }
  return { result: 'stuck', best };
}

/** A deal of `game` to give the player after deals they did not win: the first random deal the search can
 *  win within `budgetMs` (`proven`), else the one it got furthest in (the easiest it saw). `rng` and `now`
 *  are injected (the worker passes Math.random and performance.now). */
export function findHelpfulDeal(
  game: GameId,
  opts: { budgetMs: number; nodes: number; rng: () => number; now: () => number },
): { pattern: number[]; proven: boolean; tried: number } {
  const t0 = opts.now();
  const empty = createGameState(game, { scoring: 'standard', autoFlip: true });
  let best: { pattern: number[]; v: number } | null = null;
  let tried = 0;
  do {
    const dealt = deal(empty, null, opts.rng);
    tried += 1;
    const r = solve(dealt, opts.nodes);
    if (r.result === 'won') return { pattern: dealt.pattern.slice(), proven: true, tried };
    if (r.result !== 'stuck' && (!best || r.best > best.v)) best = { pattern: dealt.pattern.slice(), v: r.best };
  } while (opts.now() - t0 < opts.budgetMs);
  return { pattern: best ? best.pattern : deal(empty, null, opts.rng).pattern.slice(), proven: false, tried };
}

/** A position as a string for the exact search: cards by id (the two copies of a card in a two-deck game are the
 *  same card for the outcome), face-down cards marked, the columns as a set, each foundation by its size and top card (Forty Thieves has
 *  two piles per suit: sizes alone would mix up which suit is where), FreeCell's cells as a set, the recycles when they
 *  are limited. */
function exactKeyOf(s: GameState): string {
  const id = (k: number) => s.deck[k]!.id;
  const rules = rulesOf(s);
  // columns, foundations and cells in sorted order: which column holds a pile does not change the outcome (but in
  // Pyramid every pile is a place in the pyramid: which one is empty decides what is free)
  const piles = s.piles.tableau.map((col) => col.map((k) => (s.faceUp[k] ? id(k) : `~${id(k)}`)).join(','));
  const cols = (rules.pairs ? piles : piles.sort()).join('|');
  const found = s.piles.foundations.map((f) => (f.length ? `${f.length}${id(f[f.length - 1]!)}` : '')).sort().join(',');
  const cells = s.piles.cells.length ? `#${s.piles.cells.map((c) => c.map(id).join(',')).sort().join(',')}` : '';
  const recycles = rules.maxRecycles !== undefined ? `#${s.recycles}` : '';
  return `${cols}#${found}#${s.piles.stock.map(id).join(',')}#${s.piles.waste.map(id).join(',')}${cells}${recycles}`;
}

/** Every move from `s` (no pruning, unlike `successors`): each liftable run to every column, foundation or free cell
 *  that takes it, the waste's top card, a free cell's card, Pyramid's stock top (a King alone, or a pair with the
 *  waste), a foundation's top card back to a column (where the rules allow it), the stock. Moving a whole column to
 *  an empty one is left out (the same position). */
function allMoves(s: GameState): GameState[] {
  const out: GameState[] = [];
  const rules = rulesOf(s);
  s.piles.tableau.forEach((col, c) => {
    for (let i = 0; i < col.length; i++) {
      if (!s.faceUp[col[i]!] || !canLift(s, T(c), i)) continue;
      for (const to of legalTargets(s, { pile: T(c), index: i })) {
        if (i === 0 && to.kind === 'tableau' && s.piles.tableau[to.index]!.length === 0) continue;
        const n = moveRun(s, { pile: T(c), index: i }, to);
        if (n) out.push(n);
      }
    }
  });
  const waste = s.piles.waste;
  if (waste.length) {
    for (const to of legalTargets(s, { pile: { kind: 'waste' }, index: waste.length - 1 })) {
      const n = moveRun(s, { pile: { kind: 'waste' }, index: waste.length - 1 }, to);
      if (n) out.push(n);
    }
  }
  // FreeCell: a card in a free cell to a column or a foundation (not to another cell: the same position)
  s.piles.cells.forEach((cell, index) => {
    if (!cell.length) return;
    for (const to of legalTargets(s, { pile: { kind: 'cell', index }, index: 0 })) {
      if (to.kind === 'cell') continue;
      const n = moveRun(s, { pile: { kind: 'cell', index }, index: 0 }, to);
      if (n) out.push(n);
    }
  });
  // Pyramid: the stock's top card is in play (pairs with the waste's top are found from the waste above)
  if (rules.pairs && s.piles.stock.length) {
    const from = { pile: { kind: 'stock' } as PileRef, index: s.piles.stock.length - 1 };
    if (canLift(s, from.pile, from.index)) {
      for (const to of legalTargets(s, from)) {
        if (to.kind === 'waste') continue;
        const n = moveRun(s, from, to);
        if (n) out.push(n);
      }
    }
  }
  if (rules.foundationToTableau) {
    s.piles.foundations.forEach((f, index) => {
      if (!f.length) return;
      const from = { pile: { kind: 'foundation', index } as PileRef, index: f.length - 1 };
      for (const to of legalTargets(s, from)) {
        if (to.kind !== 'tableau') continue;
        const n = moveRun(s, from, to);
        if (n) out.push(n);
      }
    });
  }
  const stock = clickStock(s);
  if (stock) out.push(stock);
  return out;
}

/** Can the game in position `s` still be won (the "No more moves" notice, owner 2026-10-26: off by default)? Every
 *  move is tried, so `stuck` is a proof that no line wins; `unknown` when `maxNodes` positions or `budgetMs` ran out
 *  first (the notice then stays quiet). It knows the face-down cards: "stuck" means these cards cannot be won, not
 *  only that the player sees no move. Klondike, Double Klondike, Spider, Forty Thieves, FreeCell and Pyramid (not
 *  Mahjong's tiles, ui/mahjongHelp.ts has its own). Face-down column tops are turned first (a click turns them when the auto-flip is off). */
export function canStillWin(start: GameState, opts: { maxNodes: number; budgetMs: number; now: () => number }): SolveResult {
  if (start.won) return 'won';
  const t0 = opts.now();
  let faceUp = start.faceUp;
  for (const col of start.piles.tableau) {
    const top = col[col.length - 1];
    if (top !== undefined && !faceUp[top]) faceUp = faceUp.map((up, k) => up || k === top);
  }
  const first: GameState = { ...start, faceUp, autoFlip: true, history: [] };
  const seen = new Set<string>([exactKeyOf(first)]);
  const open = new Heap();
  open.push(first, score(first));
  let nodes = 0;
  while (open.size) {
    const s = open.pop()!;
    if (++nodes > opts.maxNodes || (nodes % 256 === 0 && opts.now() - t0 > opts.budgetMs)) return 'unknown';
    for (const next of allMoves({ ...s, history: [] })) {
      if (next.won || certainWin(next)) return 'won';
      const key = exactKeyOf(next);
      if (seen.has(key)) continue;
      seen.add(key);
      open.push(next, score(next));
    }
  }
  return 'stuck';
}
